import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ClockCircleOutlined } from '@ant-design/icons';
import { Button, DatePicker, InputNumber, Select, Space, TimePicker, Typography, Radio } from 'antd';
import _ from 'lodash';
import moment from 'moment';

import { disabledMinutes, updateFilterItemInList } from '../../../../../../../../../helpers/utils/reports';
import { useActions } from '../../../../../../../hooks/useActions';
import { useForceUpdate } from '../../../../../../../hooks/useForceUpdate';
import { useTypedSelector } from '../../../../../../../hooks/useTypedSelector';

/** Парсит строку времени "HH:mm" в объект {hour, minute, second} */
const parseTimeToObj = (
    timeStr: string | null | undefined,
    fallback: { hour: number; minute: number; second: number }
): { hour: number; minute: number; second: number } => {
    if (!timeStr) return fallback;
    const m = moment(timeStr, 'HH:mm');
    if (!m.isValid()) return fallback;
    return { hour: m.hour(), minute: m.minute(), second: 0 };
};

/**
 * TimePicker с автозакрытием при выборе минут.
 */
const NTP = ({ value, placeholder, disabled, onChange, disabledHours, getDisabledMinutes }: any) => {
    const _val = value ? moment(value, 'HH:mm') : null;
    const [ open, setOpen ] = useState(false);
    const lastMinuteRef = useRef<number | null>(null);

    const handleOpenChange = useCallback((newOpen: boolean) => {
        setOpen(newOpen);
        if (newOpen) {
            lastMinuteRef.current = _val ? _val.minute() : null;
        }
    }, [ _val ]);

    const handleSelect = useCallback((val: moment.Moment) => {
        onChange(val);
        const newMinute = val.minute();
        if (lastMinuteRef.current !== null && lastMinuteRef.current !== newMinute) {
            setOpen(false);
        }
        lastMinuteRef.current = newMinute;
    }, [ onChange ]);

    return (
        <TimePicker
            disabled={disabled}
            placeholder={placeholder}
            format="HH:mm"
            disabledHours={disabledHours}
            disabledMinutes={getDisabledMinutes || disabledMinutes}
            hideDisabledOptions
            showNow={false}
            value={_val}
            open={open}
            onOpenChange={handleOpenChange}
            onSelect={handleSelect}
            style={{ width: 90 }}
        />
    );
};

/**
 * DatePicker с автозакрытием при выборе минут (когда showTime включён).
 */
const DP = ({ value, type, disabled, format, showTime, onChange, disabledDate, disabledTime }: any) => {
    const _val = value ? moment(value) : null;
    const [ open, setOpen ] = useState(false);
    const lastMinuteRef = useRef<number | null>(null);

    const handleOpenChange = useCallback((newOpen: boolean) => {
        setOpen(newOpen);
        if (newOpen) {
            lastMinuteRef.current = _val ? _val.minute() : null;
        }
    }, [ _val ]);

    const handleSelect = useCallback((val: moment.Moment) => {
        onChange(val, type);
        const newMinute = val.minute();
        if (lastMinuteRef.current !== null && lastMinuteRef.current !== newMinute) {
            setOpen(false);
        }
        lastMinuteRef.current = newMinute;
    }, [ type, onChange ]);

    const openProps = showTime ? {
        open,
        onOpenChange: handleOpenChange,
        onSelect: handleSelect,
    } : {};

    return (
        <DatePicker
            disabled={disabled}
            value={_val}
            onChange={(date: any) => onChange(date, type)}
            format={format}
            showTime={showTime}
            disabledDate={disabledDate}
            disabledTime={disabledTime}
            disabledMinutes={disabledMinutes}
            style={{ width: 160 }}
            {...openProps}
        />
    );
};

const DateTimeRangePicker = ({ disabled = false, id, index, modalVisibleHandler, prefix, ...props }: any) => {
    const defVal = {
        start: null,
        end: null,
        calendar: { type: 'selectdates', time: false, nStart: null, nEnd: null },
        ...props,
    };

    const forceUpdate = useForceUpdate();
    const filters = useTypedSelector((state) => state.compare.compareFilters[`compareFilters${prefix}`]);
    const { setCompareFilter, setMetaFilterUpdate } = useActions();

    const [ isEditing, setIsEditing ] = useState(false);
    const [ filterMode, setFilterMode ] = useState(1);
    const [ _id, _setId ] = useState(id);

    // Приводим id к строке для безопасной работы с методами строк
    const safeId = String(id ?? '');

    useEffect(() => {
        forceUpdate();
    }, [ props.itemData, index, id ]);

    useEffect(() => {
        let _tempId = safeId;

        if (filterMode === 2) {
            setFilterMode(2);
            if (!safeId.includes('not_')) {
                _tempId = `not_${safeId}`;
            }
        } else {
            setFilterMode(1);
            if (safeId.includes('not_')) {
                _tempId = safeId.split('not_')[1];
            } else {
                _tempId = safeId;
            }
        }

        _setId(_tempId);
    }, [ safeId, filterMode ]);

    useEffect(() => {
        if (safeId.includes('not_')) {
            setFilterMode(2);
        } else {
            setFilterMode(1);
        }
    }, [safeId]);

    const filter = useMemo(() => {
        let _filter = props.itemData ? props.itemData : null;
        if (!props.list) {
            _filter = null;
        } else if (!isEditing) {
            setIsEditing(true);
        }

        return _filter ? _filter : defVal;
    }, [ filters, _id, index, props.list, props.itemData, props ]);

    const [ currentData, setCurrentData ] = useState(filter);

    const format = `DD.MM.YYYY${currentData.calendar.time ? ' HH:mm' : ''}`;

    // Время включается только когда пользователь явно его выбрал
    const hasTime = currentData.calendar.type === 'nhoursago'
        || (currentData.calendar.type === 'ndaysago' && currentData.calendar.nStartTime != null)
        || currentData.calendar.time;
    const finalFormat = hasTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';

    const showTime = currentData.calendar.time && { format: 'HH:mm', hideDisabledOptions: true };

    const onApply = () => {
        let _currentData = {
            ...currentData,
            id: _id,
            start: currentData.start ? moment(currentData.start).format(finalFormat) : moment().format(finalFormat),
            end: currentData.end ? moment(currentData.end).format(finalFormat) : moment().format(finalFormat)
        };

        if (isEditing) {
            let _filters = _.cloneDeep(filters);

            const payload = { ..._currentData };
            let _new_filters = updateFilterItemInList(_filters, index, payload);

            setCompareFilter({ index: prefix, filters: _new_filters });
            setMetaFilterUpdate({ value:true });
            modalVisibleHandler(false);

            return;
        }

        const payload = {
            ..._currentData,
            id: _id
        };

        if (_id === 'uwe_data_webinar' || _id === 'uwe_time_webinar' || _id === 'time_webinar') {
            const duplicateFilter = filters.find((item: any) => item.id === _id);

            if (duplicateFilter) {
                modalVisibleHandler(false);
                return;
            }
        } else if (_id === 'not_uwe_time_webinar' || _id === 'not_uwe_data_webinar' || _id === 'not_time_webinar') {
            const duplicateFilter = filters.find((item: any) => item.id === _id);

            if (duplicateFilter) {
                modalVisibleHandler(false);
                return;
            }
        }

        let _filters = _.cloneDeep(filters);
        _filters.push(payload);

        setCompareFilter({ index: prefix, filters: _filters });
        setMetaFilterUpdate({ value:true });
        modalVisibleHandler(false);
    };

    const onChangeDate = (date: any, type: string) => {

        if (type === 'start') {
            if (!currentData.calendar.time) date && date.set(startTime);
            setCurrentData((prevState: any) => {
                return {
                    ...prevState,
                    start: date
                };
            });
        } else if (type === 'end') {
            if (!currentData.calendar.time) date && date.set(endTime);
            setCurrentData((prevState: any) => {
                return {
                    ...prevState,
                    end: date
                };
            });
        }
    };

    const onChangeType = (val: string) => {
        const { start, end } = getStartEndType({ calendar: { type: val } });

        setCurrentData((prevState: any) => {
            return {
                ...prevState,
                calendar: { ...currentData.calendar, nStart: 0, nEnd: 0, nStartTime: null, nEndTime: null, type: val },
                start,
                end
            };
        });
    };

    const onClickTime = () => {
        setCurrentData((prevState: any) => {
            return {
                ...prevState,
                calendar: { ...currentData.calendar, time: !currentData.calendar.time }
            };
        });
    };

    // Переключение выбора времени в режиме "N дней назад"
    const onClickNTime = () => {
        setCurrentData((prevState: any) => {
            const hasTime = prevState.calendar.nStartTime != null;
            const newCalendar = hasTime
                ? { ...prevState.calendar, nStartTime: null, nEndTime: null }
                : { ...prevState.calendar, nStartTime: '00:00', nEndTime: '23:55' };

            const _nStart = prevState.calendar.nStart ?? 0;
            const _nEnd = prevState.calendar.nEnd ?? 0;
            const sTime = parseTimeToObj(newCalendar.nStartTime, startTime);
            const eTime = parseTimeToObj(newCalendar.nEndTime, endTime);
            let start = moment().subtract(_nStart, 'days').set(sTime);
            let end = moment().subtract(_nEnd, 'days').set(eTime);
            if (start.isAfter(end)) [start, end] = [end, start];

            return { ...prevState, start, end, calendar: newCalendar };
        });
    };

    // Изменение времени в режиме "N дней назад"
    const onChangeNTime = (nType: 'nStartTime' | 'nEndTime', val: moment.Moment | null) => {
        const timeStr = val ? val.format('HH:mm') : null;
        setCurrentData((prevState: any) => {
            const newCalendar = { ...prevState.calendar, [nType]: timeStr };

            if (prevState.calendar.type === 'ndaysago') {
                const _nStart = prevState.calendar.nStart ?? 0;
                const _nEnd = prevState.calendar.nEnd ?? 0;
                const sTime = parseTimeToObj(
                    nType === 'nStartTime' ? timeStr : prevState.calendar.nStartTime, startTime
                );
                const eTime = parseTimeToObj(
                    nType === 'nEndTime' ? timeStr : prevState.calendar.nEndTime, endTime
                );
                let start = moment().subtract(_nStart, 'days').set(sTime);
                let end = moment().subtract(_nEnd, 'days').set(eTime);
                if (start.isAfter(end)) [start, end] = [end, start];
                return { ...prevState, start, end, calendar: newCalendar };
            }

            return { ...prevState, calendar: newCalendar };
        });
    };

    const onChangeN = (nType: string, val: any) => {
        let _nStart = currentData.calendar.nStart ? currentData.calendar.nStart : 0;
        let _nEnd = currentData.calendar.nEnd ? currentData.calendar.nEnd : 0;
        if (nType === 'nStart') _nStart = val;
        if (nType === 'nEnd') _nEnd = val;
        let start = moment();
        let end = moment();
        if (currentData.calendar.type === 'ndaysago') {
            const sTime = parseTimeToObj(currentData.calendar.nStartTime, startTime);
            const eTime = parseTimeToObj(currentData.calendar.nEndTime, endTime);
            start = start.subtract(_nEnd + _nStart, 'days').set(sTime);
            end = end.subtract(_nStart, 'days').set(eTime);
        }
        if (currentData.calendar.type === 'nhoursago') {
            start = start.subtract(_nEnd + _nStart, 'hours');
            end = end.subtract(_nStart, 'hours');
        }

        setCurrentData((prevState: any) => {
            return {
                ...prevState,
                start,
                end,
                calendar: { ...currentData.calendar, [nType]: val },
            };
        });
    };

    // --- Ограничения: ОТ всегда раньше ДО ---
    const baseDisabled = useMemo(() => disabledMinutes(), []);

    const startDisabledDate = useCallback((current: moment.Moment) => {
        if (!currentData.end || !current) return false;
        return current.isAfter(moment(currentData.end), 'day');
    }, [currentData.end]);

    const endDisabledDate = useCallback((current: moment.Moment) => {
        if (!currentData.start || !current) return false;
        return current.isBefore(moment(currentData.start), 'day');
    }, [currentData.start]);

    const startDisabledTime = useCallback((current: moment.Moment | null) => {
        if (!current || !currentData.end) return { disabledMinutes: () => baseDisabled };
        const endM = moment(currentData.end);
        if (!current.isSame(endM, 'day')) return { disabledMinutes: () => baseDisabled };
        const eH = endM.hour();
        const eMin = endM.minute();
        return {
            disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter(h => h > eH),
            disabledMinutes: (hour: number) => {
                if (hour < eH) return baseDisabled;
                if (hour === eH) {
                    const range = Array.from({ length: 60 }, (_, i) => i).filter(m => m > eMin);
                    return [ ...new Set([ ...baseDisabled, ...range ]) ];
                }
                return Array.from({ length: 60 }, (_, i) => i);
            },
        };
    }, [currentData.end, baseDisabled]);

    const endDisabledTime = useCallback((current: moment.Moment | null) => {
        if (!current || !currentData.start) return { disabledMinutes: () => baseDisabled };
        const startM = moment(currentData.start);
        if (!current.isSame(startM, 'day')) return { disabledMinutes: () => baseDisabled };
        const sH = startM.hour();
        const sMin = startM.minute();
        return {
            disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter(h => h < sH),
            disabledMinutes: (hour: number) => {
                if (hour > sH) return baseDisabled;
                if (hour === sH) {
                    const range = Array.from({ length: 60 }, (_, i) => i).filter(m => m < sMin);
                    return [ ...new Set([ ...baseDisabled, ...range ]) ];
                }
                return Array.from({ length: 60 }, (_, i) => i);
            },
        };
    }, [currentData.start, baseDisabled]);

    // --- Ограничения для времени в режиме ndaysago ---
    const nStartDisabledHours = useCallback(() => {
        const nS = currentData.calendar.nStart ?? 0;
        const nE = currentData.calendar.nEnd ?? 0;
        if (nS !== nE || !currentData.calendar.nEndTime) return [];
        const endM = moment(currentData.calendar.nEndTime, 'HH:mm');
        if (!endM.isValid()) return [];
        return Array.from({ length: 24 }, (_, i) => i).filter(h => h > endM.hour());
    }, [currentData.calendar.nStart, currentData.calendar.nEnd, currentData.calendar.nEndTime]);

    const nStartGetDisabledMinutes = useCallback((hour: number) => {
        const nS = currentData.calendar.nStart ?? 0;
        const nE = currentData.calendar.nEnd ?? 0;
        if (nS !== nE || !currentData.calendar.nEndTime) return baseDisabled;
        const endM = moment(currentData.calendar.nEndTime, 'HH:mm');
        if (!endM.isValid()) return baseDisabled;
        if (hour < endM.hour()) return baseDisabled;
        if (hour === endM.hour()) {
            const range = Array.from({ length: 60 }, (_, i) => i).filter(m => m > endM.minute());
            return [ ...new Set([ ...baseDisabled, ...range ]) ];
        }
        return Array.from({ length: 60 }, (_, i) => i);
    }, [currentData.calendar.nStart, currentData.calendar.nEnd, currentData.calendar.nEndTime, baseDisabled]);

    const nEndDisabledHours = useCallback(() => {
        const nS = currentData.calendar.nStart ?? 0;
        const nE = currentData.calendar.nEnd ?? 0;
        if (nS !== nE || !currentData.calendar.nStartTime) return [];
        const startM = moment(currentData.calendar.nStartTime, 'HH:mm');
        if (!startM.isValid()) return [];
        return Array.from({ length: 24 }, (_, i) => i).filter(h => h < startM.hour());
    }, [currentData.calendar.nStart, currentData.calendar.nEnd, currentData.calendar.nStartTime]);

    const nEndGetDisabledMinutes = useCallback((hour: number) => {
        const nS = currentData.calendar.nStart ?? 0;
        const nE = currentData.calendar.nEnd ?? 0;
        if (nS !== nE || !currentData.calendar.nStartTime) return baseDisabled;
        const startM = moment(currentData.calendar.nStartTime, 'HH:mm');
        if (!startM.isValid()) return baseDisabled;
        if (hour > startM.hour()) return baseDisabled;
        if (hour === startM.hour()) {
            const range = Array.from({ length: 60 }, (_, i) => i).filter(m => m < startM.minute());
            return [ ...new Set([ ...baseDisabled, ...range ]) ];
        }
        return Array.from({ length: 60 }, (_, i) => i);
    }, [currentData.calendar.nStart, currentData.calendar.nEnd, currentData.calendar.nStartTime, baseDisabled]);

    return (
        <Space direction="vertical" style={{ width: 390 }}>
            <div style={{ marginTop: 8 }}>
                <Radio.Group disabled={disabled} onChange={(e) => setFilterMode(e.target.value)} value={filterMode}>
                    <Radio value={1}>Включить</Radio>
                    <Radio value={2}>Исключить</Radio>
                </Radio.Group>
            </div>
            <div>
                <Select disabled={disabled} value={currentData.calendar.type} onChange={onChangeType} style={{ width: '100%', marginBottom: 5 }}>
                    <Select.Option value="selectdates">выбрать даты</Select.Option>
                    <Select.Option value="today">сегодня</Select.Option>
                    <Select.Option value="yesterday">вчера</Select.Option>
                    <Select.Option value="thisweek">эта неделя</Select.Option>
                    <Select.Option value="lastweek">прошлая неделя</Select.Option>
                    <Select.Option value="thismonth">этот месяц</Select.Option>
                    <Select.Option value="lastmonth">прошлый месяц</Select.Option>
                    <Select.Option value="thisquart">этот квартал</Select.Option>
                    <Select.Option value="lastquart">прошлый квартал</Select.Option>
                    <Select.Option value="thisyear">этот год</Select.Option>
                    <Select.Option value="lastyear">прошлый год</Select.Option>
                    <Select.Option value="last7days">последние 7 дней</Select.Option>
                    <Select.Option value="last14days">последние 14 дней</Select.Option>
                    <Select.Option value="last30days">последние 30 дней</Select.Option>
                    <Select.Option value="last365days">последние 365 дней</Select.Option>
                    <Select.Option value="ndaysago">N дней назад</Select.Option>
                    <Select.Option value="nhoursago">N часов назад</Select.Option>

                </Select>
            </div>
            <div>
                {currentData.calendar.type === 'selectdates' && (
                    <Space size="small">
                        <Button disabled={disabled} onClick={onClickTime} icon={<ClockCircleOutlined />} type={currentData.calendar.time ? 'link' : 'text'} />
                        <DP value={currentData.start} type="start" disabled={disabled} format={format} showTime={showTime} onChange={onChangeDate} disabledDate={startDisabledDate} disabledTime={startDisabledTime} />
                        <Typography.Text type="secondary">—</Typography.Text>
                        <DP value={currentData.end} type="end" disabled={disabled} format={format} showTime={showTime} onChange={onChangeDate} disabledDate={endDisabledDate} disabledTime={endDisabledTime} />
                    </Space>
                )}
                {[ 'ndaysago', 'nhoursago' ].includes(currentData.calendar.type) && (
                    <Space size="small" direction="vertical">
                        <Space size="small">
                            <Typography.Text type="secondary">от</Typography.Text>
                            <InputNumber min={0} disabled={disabled} value={currentData.calendar.nStart} onChange={(val) => onChangeN('nStart', val)} style={{ width: 100 }} />
                            <Typography.Text type="secondary">до</Typography.Text>
                            <InputNumber min={0} disabled={disabled} value={currentData.calendar.nEnd} onChange={(val) => onChangeN('nEnd', val)} style={{ width: 100 }} />
                        </Space>
                        {currentData.calendar.type === 'ndaysago' && (
                            <Space size="small">
                                <Button
                                    disabled={disabled}
                                    onClick={onClickNTime}
                                    icon={<ClockCircleOutlined />}
                                    type={currentData.calendar.nStartTime != null ? 'link' : 'text'}
                                    size="small"
                                />
                                {currentData.calendar.nStartTime != null && (
                                    <>
                                        <NTP
                                            disabled={disabled}
                                            placeholder="с"
                                            value={currentData.calendar.nStartTime}
                                            onChange={(val: any) => onChangeNTime('nStartTime', val)}
                                            disabledHours={nStartDisabledHours}
                                            getDisabledMinutes={nStartGetDisabledMinutes}
                                        />
                                        <Typography.Text type="secondary">—</Typography.Text>
                                        <NTP
                                            disabled={disabled}
                                            placeholder="по"
                                            value={currentData.calendar.nEndTime}
                                            onChange={(val: any) => onChangeNTime('nEndTime', val)}
                                            disabledHours={nEndDisabledHours}
                                            getDisabledMinutes={nEndGetDisabledMinutes}
                                        />
                                    </>
                                )}
                            </Space>
                        )}
                    </Space>
                )}
            </div>
            {![ 'selectdates', 'allthetime' ].includes(currentData.calendar.type) && (
                <ShowStartEnd start={currentData.start} end={currentData.end} calendar={currentData.calendar} />
            )}
            <Button disabled={disabled} type="primary" style={{ marginTop: 10 }} onClick={onApply}>
                Применить Hi!
            </Button>
        </Space>
    );
};
const startTime = { hour: 0, minute: 0, second: 0 };
const endTime = { hour: 23, minute: 59, second: 59 };
const FORMAT_VIEW_TIME = 'DD MMM YYYY HH:mm';
const FORMAT_VIEW_DATE = 'DD MMM YYYY';

function ShowStartEnd({ start: _start, end: _end, calendar }: any) {
    const { start, end } = getStartEndType({ start: _start, end: _end, calendar });

    // Время в превью только если оно явно выбрано
    const hasTimeView = calendar.type === 'nhoursago'
        || (calendar.type === 'ndaysago' && calendar.nStartTime != null)
        || calendar.time;
    const viewFmt = hasTimeView ? FORMAT_VIEW_TIME : FORMAT_VIEW_DATE;

    const showStartDate = useMemo(() => moment(start).format('dd').toUpperCase(), [ start ]);
    const showStartTime = useMemo(() => moment(start).format(viewFmt), [ start, viewFmt ]);
    const showEndDate = useMemo(() => moment(end).format('dd').toUpperCase(), [ end ]);
    const showEndTime = useMemo(() => moment(end).format(viewFmt), [ end, viewFmt ]);

    return (
        <Space size="small">
            <div>
                {showStartDate}&nbsp;{showStartTime}
            </div>
            <Typography.Text type="secondary">—</Typography.Text>
            <div>
                {showEndDate}&nbsp;{showEndTime}
            </div>
        </Space>
    );
}

export const getStartEndType = ({ start = null, end = null, calendar }: any) => {
    const { type } = calendar;
    if (type === 'today') {
        start = moment().set(startTime);
        end = moment().set(endTime);
    } else if (type === 'yesterday') {
        start = moment().subtract(1, 'days').set(startTime);
        end = moment().subtract(1, 'days').set(endTime);
    }else if(type === 'last7days'){
        start = moment().subtract(6, 'days').set(startTime);
        end = moment().set(endTime);
    }
    else if(type === 'last14days'){
        start = moment().subtract(13, 'days').set(startTime);
        end = moment().set(endTime);
    }  else if (type === 'thisweek') {
        start = moment().isoWeekday(1).set(startTime);
        end = moment().set(endTime);
    } else if (type === 'lastweek') {
        start = moment().subtract(1, 'week').isoWeekday(1).set(startTime);
        end = moment().subtract(1, 'week').isoWeekday(7).set(endTime);
    } else if (type === 'thismonth') {
        start = moment().startOf('month').set(startTime);
        end = moment().set(endTime);
    } else if (type === 'lastmonth') {
        start = moment().subtract(1, 'month').startOf('month').set(startTime);
        end = moment().subtract(1, 'month').endOf('month').set(endTime);
    } else if (type === 'last30days') {
        start = moment().subtract(29, 'days').set(startTime);
        end = moment().set(endTime);
    }else if(type ==='thisquart'){
        start = moment().startOf('quarters').set(startTime);
        end = moment().set(startTime);
    }else if(type === 'lastquart'){
        start = moment().subtract(1, 'quarters').startOf('quarters').set(startTime);
        end = moment().subtract(1, 'quarters').endOf('quarters').set(startTime);
    }else if(type === 'thisyear'){
        start = moment().startOf('year').set(startTime);
        end = moment();
    }else if(type === 'lastyear'){
        start = moment().subtract(1, 'year').startOf('year').set(startTime);
        end = moment().subtract(1, 'year').endOf('year').set(startTime);
    }
    else if(type === 'last365days'){
        start = moment().subtract(365, 'days').set(startTime);
        end = moment().set(endTime);
    } else if (type === 'ndaysago') {
        const { nStart, nEnd, nStartTime, nEndTime } = calendar;
        const sTime = parseTimeToObj(nStartTime, startTime);
        const eTime = parseTimeToObj(nEndTime, endTime);
        start = moment().subtract(nEnd, 'days').set(sTime);
        end = moment().subtract(nStart, 'days').set(eTime);
    } else if (type === 'nhoursago') {
        const { nStart, nEnd } = calendar;
        start = moment().subtract(nEnd, 'hours');
        end = moment().subtract(nStart, 'hours');
    }
    return { start, end };
};

export default DateTimeRangePicker;
